import React, { useEffect, useRef, useState, KeyboardEvent } from 'react'
import Icon from './icon'
import { useClickOutside } from './util'
import { buttonClassName } from './button/utils'

export type SelectItemProps = {
  id: string | number
  label: string
} & object

type SelectProps<T extends SelectItemProps> = {
  name: string
  items: T[]
  selectedItems: T[]
  color?: 'primary' | 'secondary'
  invert?: boolean
  itemComponent: React.FC<T>
  searchable?: boolean
  label?: string
  required?: boolean
  error?: string
} & (
    | {
      multi: true
      onSelect?: (val: T[]) => void
    }
    | {
      multi: false
      onSelect?: (val: T | undefined) => void
    }
  )

const Select = <T extends SelectItemProps>({
  name,
  items,
  selectedItems,
  color = 'primary',
  invert = false,
  multi,
  onSelect,
  itemComponent: ItemComponent,
  searchable = false,
  label,
  required,
  error,
}: SelectProps<T>) => {
  const [isDisplay, setIsDisplay] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLUListElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const filteredItems = items.filter(item =>
    item.label.toLowerCase().includes(searchValue.toLowerCase())
  )

  useClickOutside(wrapperRef, () => {
    setIsDisplay(false)
    setFocusedIndex(-1)
  })

  const [currentSelection, setCurrentSelection] = useState<Record<string, T>>(
    selectedItems.reduce((prev, cur) => ({ ...prev, [cur.id]: cur }), {})
  )

  useEffect(() => {
    setCurrentSelection(
      selectedItems.reduce((prev, cur) => ({ ...prev, [cur.id]: cur }), {})
    )
  }, [selectedItems])

  const handleOnSelect = (val: Record<string, T>) => {
    if (multi && onSelect) onSelect(Object.values(val))
    if (!multi && onSelect) {
      onSelect(Object.keys(val).length > 0 ? Object.values(val)[0] : undefined)
      setIsDisplay(false)
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isDisplay) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        setIsDisplay(true)
      }
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setFocusedIndex(prev =>
          prev < filteredItems.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        event.preventDefault()
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (focusedIndex >= 0) {
          const item = filteredItems[focusedIndex]
          if (multi) {
            const newItems = { ...currentSelection }
            if (currentSelection[item.id]) {
              delete newItems[item.id]
            } else {
              newItems[item.id] = item
            }
            setCurrentSelection(newItems)
            handleOnSelect(newItems)
          } else {
            setCurrentSelection({ [item.id]: item })
            handleOnSelect({ [item.id]: item })
          }
        }
        break
      case 'Escape':
        event.preventDefault()
        setIsDisplay(false)
        setFocusedIndex(-1)
        buttonRef.current?.focus()
        break
      case 'Tab':
        setIsDisplay(false)
        setFocusedIndex(-1)
        break
      default:
        break
    }
  }

  const handleItemKeyDown = (event: KeyboardEvent, item: T) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (multi) {
        const newItems = { ...currentSelection }
        if (currentSelection[item.id]) {
          delete newItems[item.id]
        } else {
          newItems[item.id] = item
        }
        setCurrentSelection(newItems)
        handleOnSelect(newItems)
      } else {
        setCurrentSelection({ [item.id]: item })
        handleOnSelect({ [item.id]: item })
      }
    }
  }

  const selectId = `select-${name}`
  const listboxId = `${selectId}-listbox`
  const errorId = error ? `${selectId}-error` : undefined

  return (
    <div ref={wrapperRef} className="relative flex flex-col">
      {label && (
        <label htmlFor={selectId} className="mb-1 text-sm font-medium">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <button
        ref={buttonRef}
        id={selectId}
        type="button"
        onClick={() => setIsDisplay(!isDisplay)}
        className={buttonClassName({ color, invert })}
        aria-expanded={isDisplay}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        onKeyDown={handleKeyDown}
      >
        <div className="flex flex-row items-center gap-4 text-sm">
          {name} <Icon icon="caret" className="w-3 h-3" aria-hidden="true" />
        </div>
      </button>

      {isDisplay && items.length > 0 && (
        <ul
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          className="absolute z-10 flex flex-col items-stretch justify-center gap-2 py-4 overflow-y-auto bg-white whitespace-nowrap rounded-xl top-12"
          aria-multiselectable={multi}
          aria-label={name}
          tabIndex={-1}
        >
          {searchable && (
            <li role="none" className="px-4">
              <input
                type="search"
                placeholder="Search"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full px-3 py-2 border rounded-full focus:outline-none focus:border-primary"
                aria-label="Search options"
              />
            </li>
          )}
          {filteredItems.map((item, index) => (
            <li
              key={item.id}
              role="option"
              aria-selected={!!currentSelection[item.id]}
              className={`flex flex-row items-center gap-2 px-2 text-sm text-left hover:bg-primary-50 lg:px-4 lg:py-1 focus:outline-none ${index === focusedIndex ? 'bg-primary-50' : ''
                }`}
              onClick={() => {
                if (multi) {
                  const newItems = { ...currentSelection }
                  if (currentSelection[item.id]) {
                    delete newItems[item.id]
                  } else {
                    newItems[item.id] = item
                  }
                  setCurrentSelection(newItems)
                  handleOnSelect(newItems)
                } else {
                  setCurrentSelection({ [item.id]: item })
                  handleOnSelect({ [item.id]: item })
                }
              }}
              onKeyDown={(e) => handleItemKeyDown(e, item)}
              tabIndex={0}
            >
              {multi && (
                <input
                  type="checkbox"
                  readOnly
                  checked={!!currentSelection[item.id]}
                  className="rounded-md focus:outline-none hover:outline-none active:outline-none checked:outline-none text-secondary border-secondary"
                  aria-hidden="true"
                />
              )}
              <ItemComponent {...item} />
            </li>
          ))}
        </ul>
      )}
      {error && (
        <div id={errorId} role="alert" className="mt-1 text-sm text-red-500">
          {error}
        </div>
      )}
    </div>
  )
}

export default Select
